import { QuoteDocumentConfig, QuoteWithPdfFields } from '@monetize/types/app';
import { useCallback, useMemo, useState } from 'react';
import {
  doGetQuoteDocumentConfigDetails,
  doGetQuoteStorageById,
  doPrintQuoteToHtml,
  doPrintQuoteToPdf,
  useGetAttachedQuoteDocuments,
} from '../../../../api/cpqService';
import { useFlags } from '../../../../services/launchDarkly';
import { base64ToArrayBuffer } from '../../../../utils';

const highlightTextPattern = /\/m[0-9a-z]+\//i;

/** Available tabs based on what exists for this quote */
function getTabs(
  quote: QuoteWithPdfFields,
  isQuoteDocumentManagementEnabled: boolean,
  documentConfigTabs: { label: string; documentConfigId: string }[],
) {
  const output: { label: string; value: string }[] = [
    {
      label: 'Quote',
      value: 'QUOTE',
    },
  ];
  if (quote.documentLocation && !isQuoteDocumentManagementEnabled) {
    output.push({
      label: 'MSA',
      value: 'MSA',
    });
  }
  if (quote.sowDocumentStorageId && !isQuoteDocumentManagementEnabled) {
    output.push({
      label: 'SOW',
      value: 'SOW',
    });
  }

  if (isQuoteDocumentManagementEnabled) {
    documentConfigTabs.forEach(({ label }) => {
      output.push({ label, value: label });
    });
  }

  if (quote.signedDocumentStorageId) {
    output.push({
      label: 'Finalized Quote',
      value: 'SIGNED_QUOTE',
    });
  } else {
    output.push({
      label: 'Full Proposal',
      value: 'FULL_QUOTE',
    });
  }
  return output;
}

/** Prefer Signed Quote if exists, otherwise Quote */
function getInitialActiveTab(tabs: { label: string; value: string }[]) {
  const signedDocumentIdx = tabs.findIndex(
    ({ value }) => value === 'SIGNED_QUOTE',
  );
  return signedDocumentIdx > -1 ? signedDocumentIdx : 0;
}

/** Make API request to get a storage document and convert content to ArrayBuffer */
function getStorageContent(quoteId: string, storageId: string) {
  return doGetQuoteStorageById(quoteId, storageId, {
    attachContent: true,
  }).then((storage) => ({
    filename: storage.filename,
    content: base64ToArrayBuffer(storage.contentBase64Encoded || ''),
  }));
}

/**
 * Hook to contain logic for managing the quote PDF drawer, which can show multiple PDF documents in tabs
 */
export const useQuotePdfDrawer = (quote: QuoteWithPdfFields) => {
  const { enableQuoteDocumentManagement } = useFlags();
  const quoteId = quote?.id;
  const quoteDescription = quote?.description;
  const documentLocation = quote?.documentLocation;
  const sowDocumentStorageId = quote?.sowDocumentStorageId;
  const signedDocumentStorageId = quote?.signedDocumentStorageId;
  const selectDocumentConfigTabs = useCallback(
    (data: QuoteDocumentConfig[]) => {
      // Track how many times each base label has been seen to generate unique labels
      // Data arrives pre-sorted (BEFORE first, AFTER second) from the query
      const counts: Record<string, number> = {};
      return data.map((doc) => {
        const baseLabel = doc.quoteDocumentType.label;
        counts[baseLabel] = (counts[baseLabel] ?? 0) + 1;
        const count = counts[baseLabel];
        const label = count === 1 ? baseLabel : `${baseLabel} (${count - 1})`;
        return { label, documentConfigId: doc.id };
      });
    },
    [],
  );

  /**
   * Ordered list of document config tabs to show in the PDF drawer.
   * BEFORE-position documents come first, AFTER-position documents second,
   * each group sorted by sortWithinPosition.
   * If multiple documents share the same type label, a numeric suffix is
   * appended to keep tab values unique — e.g. "MSA", "MSA (1)", "MSA (2)".
   */
  const { data: documentConfigTabs = [] } = useGetAttachedQuoteDocuments(
    quoteId ?? '',
    {
      select: selectDocumentConfigTabs,
    },
  );

  const documentConfigStorageMap = useMemo(
    () =>
      documentConfigTabs.reduce<Record<string, string>>(
        (acc, { label, documentConfigId }) => {
          acc[label] = documentConfigId;
          return acc;
        },
        {},
      ),
    [documentConfigTabs],
  );

  const [tabs, setTabs] = useState(() =>
    getTabs(quote, enableQuoteDocumentManagement, documentConfigTabs),
  );
  const [filename, setFilename] = useState(
    () => quote.description || 'quote-pdf',
  );
  const [useHighlight, setUseHighlight] = useState(true);
  const [defaultTab, setDefaultTab] = useState(() => getInitialActiveTab(tabs));

  const fetchDocument = useCallback(
    async (tab?: string): Promise<ArrayBuffer | string | null> => {
      let output: ArrayBuffer | string | null = null;
      if (quoteId) {
        let newFilename = '';
        let shouldHighlight = true;
        if (!tab || tab === 'FULL_QUOTE') {
          newFilename = quoteDescription || 'quote-pdf';
          output = await doPrintQuoteToPdf(quoteId);
        } else if (!tab || tab === 'QUOTE') {
          output = await doPrintQuoteToHtml(quoteId);
        } else if (tab === 'MSA' && documentLocation) {
          const { content, filename: storageFilename } =
            await getStorageContent(quoteId, documentLocation);
          output = content;
          newFilename = storageFilename;
        } else if (tab === 'SOW' && sowDocumentStorageId) {
          const { content, filename: storageFilename } =
            await getStorageContent(quoteId, sowDocumentStorageId);
          output = content;
          newFilename = storageFilename;
        } else if (tab === 'SIGNED_QUOTE' && signedDocumentStorageId) {
          const { content, filename: storageFilename } =
            await getStorageContent(quoteId, signedDocumentStorageId);
          output = content;
          newFilename = storageFilename;
          shouldHighlight = false;
        } else if (tab && tab in documentConfigStorageMap) {
          const documentConfigId = documentConfigStorageMap[tab];
          const quoteDocumentConfig = await doGetQuoteDocumentConfigDetails(
            quoteId,
            documentConfigId,
            true,
          );
          output = base64ToArrayBuffer(
            quoteDocumentConfig.storageObject.contentBase64Encoded || '',
          );
          newFilename = quoteDocumentConfig.storageObject.filename;
        }

        if (newFilename) {
          setFilename(newFilename);
        }
        setUseHighlight(shouldHighlight);
      }
      return output;
    },
    [
      documentConfigStorageMap,
      documentLocation,
      quoteDescription,
      quoteId,
      signedDocumentStorageId,
      sowDocumentStorageId,
    ],
  );

  const resetTabs = useCallback(() => {
    const newValue = getTabs(
      {
        documentLocation,
        sowDocumentStorageId,
        signedDocumentStorageId,
      } as QuoteWithPdfFields,
      enableQuoteDocumentManagement,
      documentConfigTabs,
    );
    setTabs((priorValue) => {
      return newValue.length !== priorValue.length ? newValue : priorValue;
    });
    setDefaultTab(getInitialActiveTab(newValue));
  }, [
    documentConfigTabs,
    documentLocation,
    enableQuoteDocumentManagement,
    signedDocumentStorageId,
    sowDocumentStorageId,
  ]);

  return {
    tabs,
    filename,
    highlightTextPattern: useHighlight ? highlightTextPattern : undefined,
    defaultTab,
    fetchDocument,
    resetTabs,
  };
};
